import React, { useMemo, useState } from 'react'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { Box, SxProps, Theme } from '@mui/material'
import { useField } from 'formik'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { AutoCompleteGoogleMapFieldProps } from './intefaces'
import { compactFormFieldSx } from '../DisplayedItem'
import { getInlineGeoLocationsDisplay } from './geoLocationLimits'
import { renderGeoLocationChips } from './renderGeoLocationChips'
import ViewAllGeoLocationsButton from './ViewAllGeoLocationsButton'
import GeoLocationsDialog from './GeoLocationsDialog'

type ReadOnlyAutoCompleteGoogleMapFieldProps = Pick<
  AutoCompleteGoogleMapFieldProps,
  'name' | 'label' | 'locationType' | 'compact'
> & {
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  sx?: SxProps<Theme>
}

const ReadOnlyAutoCompleteGoogleMapField: React.FC<ReadOnlyAutoCompleteGoogleMapFieldProps> = ({
  name,
  label,
  locationType = LocationTypesEnum.Cities,
  compact = false,
  onMouseEnter,
  onMouseLeave,
  sx,
}) => {
  const t = useAppTranslations()
  const [field] = useField({ name })
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const locations = useMemo(() => (Array.isArray(field.value) ? field.value : []), [field.value])
  const { displayValue, exceedsInlineLimit, totalCount } = useMemo(
    () => getInlineGeoLocationsDisplay(locations),
    [locations]
  )

  return (
    <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} sx={{ width: '100%' }}>
      <Box
        sx={{
          display: exceedsInlineLimit ? 'flex' : 'block',
          alignItems: 'flex-end',
          gap: 1,
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Autocomplete
            id="autoCompleteGoogleMapReadOnly"
            disabled
            multiple
            options={[]}
            value={exceedsInlineLimit ? displayValue : locations}
            getOptionLabel={() => ''}
            renderValue={(tagValue, getItemProps) =>
              renderGeoLocationChips(tagValue, getItemProps, locationType, { fieldName: name })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(label)}
                variant={compact ? 'standard' : 'outlined'}
                fullWidth
                margin={compact ? 'none' : 'normal'}
                size="small"
                helperText=" "
                hiddenLabel={compact}
                sx={compact ? compactFormFieldSx : sx}
              />
            )}
          />
        </Box>
        {exceedsInlineLimit && <ViewAllGeoLocationsButton count={totalCount} onClick={() => setViewDialogOpen(true)} />}
      </Box>
      {exceedsInlineLimit && (
        <GeoLocationsDialog
          open={viewDialogOpen}
          setOpen={setViewDialogOpen}
          mode="read"
          name={name}
          label={label}
          locationType={locationType}
          count={totalCount}
        />
      )}
    </Box>
  )
}

export default ReadOnlyAutoCompleteGoogleMapField
